/**
 * Which claim's full card the thread is showing, on a desktop (D354).
 *
 * The owner's design takes the node card out of the thread: the claim held is a
 * small card on the field (`FieldCard.tsx`), and its whole card — the sources and
 * checks, the recorded results, the rewrite, the panel's readings, the meters —
 * opens at the head of the thread on Details, or on any press that needs it there
 * (a record or a rewrite opening its form, the scrubber's Inspect). This is that
 * one fact, shared by the thread that shows the card and the field card that
 * stands in for it, so the two are never on screen for one claim at once.
 *
 * Module state, `composerMode.ts`'s reason: a reader's view, never the document's,
 * and read by two trees that share no parent below App. A claim id, not a flag, so
 * a new selection is a card that has not been asked for. Nothing here is stored.
 */

import { useSyncExternalStore } from 'react'

let detailsFor: string | null = null
const watchers = new Set<() => void>()

function set(next: string | null): void {
  if (next === detailsFor) return
  detailsFor = next
  for (const w of watchers) w()
}

/** The card for `id` at the head of the thread. */
export function openDetails(id: string): void {
  set(id)
}

/** Back to the conversation; the field card stands in again. */
export function closeDetails(): void {
  set(null)
}

function subscribe(fn: () => void): () => void {
  watchers.add(fn)
  return () => watchers.delete(fn)
}

const read = (): string | null => detailsFor

export function useDetailsFor(): string | null {
  return useSyncExternalStore(subscribe, read, read)
}
